import { BfxBlotterOrdersContextMenuItemQuickFillState } from "./BfxBlotterOrdersContextMenuItemQuickFillState";
import { BfxBlotterOrdersContextMenuState } from "./BfxBlotterOrdersContextMenuState";
import { BfxBlotterOrdersContextMenuItem } from "./BfxBlotterOrdersContextMenuItem";
import { BfxBlotterAction } from "../bfx-base-blotter/BfxBlotterAction";
import { ContextMenuItemState } from "../ContextMenuItemState";
import { ContextMenuItemStateCommand } from "../ContextMenuItemStateCommand";
import { ContextMenuItemStateCommandCriteria } from "../ContextMenuItemStateCommandCriteria";
import { ContextMenuState } from "../ContextMenuState";
import { TraderGroupPromiseLookupService } from "../../../lookup/model/impl/TraderGroupPromiseLookupService";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { OrderRelationshipType } from "shared/cubiq-model-multi-asset/model/enum/OrderRelationshipType";
import { SiblingRelationship } from "shared/cubiq-services/lookup/model/impl/SiblingRelationshipManager";
import { AbstractFillMenuItem } from "./AbstractFillMenuItem";

const ALGO_TERMINAL_STATES: CubiqTS.OrderState[] = [
  CubiqTS.OrderState.CANCELLED,
  CubiqTS.OrderState.FILLED,
  CubiqTS.OrderState.EXPIRED,
  CubiqTS.OrderState.REJECTED,
];

export class BfxBlotterOrdersContextMenuQuickFillItem extends AbstractFillMenuItem {
  constructor() {
    super(
      BfxBlotterAction.ACTION_ORDER_QUICKFILL,
      "Quick Fill",
      BfxBlotterOrdersContextMenuQuickFillItem.checkAvailableFn(),
      BfxBlotterOrdersContextMenuQuickFillItem.checkEnabledFn(),
      ContextMenuItemStateCommandCriteria.MenuItem,
      ContextMenuItemStateCommandCriteria.Item,
    );
    this.clearSelection = true;
  }

  private static checkAvailableFn(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: ContextMenuItemState,
    ) {
      itemState.available = menuState.canQuickFillOrders;
    };
  }

  /**
   * note- itemState.enabled startes off true for QuickFill
   * this fuction may set it false but must not set it true
   */
  private static checkEnabledFn(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: BfxBlotterOrdersContextMenuItemQuickFillState,
      item: CubiqTS.OrderDataImpl,
    ) {
      if (!item || !itemState.enabled || !itemState.available) {
        // ineligible item encountered, enabled will never be true
        return;
      }

      // Cannot Quick Fill MKT Orders
      if (item.requestedOrderType === CubiqTS.RequestOrderType.MARKET) {
        itemState.enabled = false;
        return;
      }

      // if we have an Algo order - and it has no tracking rate - quick fill is disabled
      if (
        item.requestedOrderType === CubiqTS.RequestOrderType.ALGO &&
        (item.trackingRt === null || ALGO_TERMINAL_STATES.includes(item.orderState))
      ) {
        itemState.enabled = false;
        return;
      }

      // If two siblings of an OCO are selected quick fill should be disabled as per OMS-21024
      if (
        item.siblingRelationship != null &&
        item.siblingRelationship.relationshipType === OrderRelationshipType.OCO
      ) {
        let orderId: string = item.orderId.toString();
        let relatedOrderId: string = item.siblingRelationship.orderId;

        if (itemState.relatedOrders[relatedOrderId] != null) {
          // A OCO sibling is selected, disable the quick fill
          itemState.enabled = false;
        } else {
          itemState.relatedOrders[orderId] = relatedOrderId;
        }
      }

      // Skip this check if not a gamma order
      if (item.gammaRungId != null) {
        itemState.enabled = false == AbstractFillMenuItem.isBothSidesOfGamma(item, itemState);
      }

      if (
        menuState.activeUser.isTrader() &&
        item.deskId &&
        !menuState.activeUser.tradingGroupsIdSet.has(item.deskId.toString())
      ) {
        itemState.enabled = false;
      }

      if (
        menuState.activeUser.isSales() &&
        item.salesGroupId &&
        !menuState.activeUser.salesGroupsIdSet.has(item.salesGroupId.toString())
      ) {
        itemState.enabled = false;
      }

      // Order is locked
      if (
        item.lockType !== null &&
        (item.lockType === CubiqTS.LockType.FILL_LOCK ||
          item.lockType === CubiqTS.LockType.FILL_NOW_LOCK ||
          item.lockType === CubiqTS.LockType.MODIFY_LOCK ||
          item.lockType === CubiqTS.LockType.SI_LOCK ||
          item.lockType === CubiqTS.LockType.CANCEL_LOCK ||
          item.lockType === CubiqTS.LockType.TRIGGER_LOCK)
      ) {
        itemState.enabled = false;
      }

      // Order state:   in intervention, or suspended
      switch (item.orderState) {
        case CubiqTS.OrderState.PENDING:
        case CubiqTS.OrderState.PENDING_CANCELLATION:
        // intervention
        case CubiqTS.OrderState.ACCEPTANCE_NEEDED: // Modify in Pending, New Pending
        case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED: // Modify Out Pending
        case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED: // Cancel Pending
        case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED: // Migration Acceptance Needed
        // suspended
        case CubiqTS.OrderState.SUSPENDED: // Suspended
        case CubiqTS.OrderState.FILL_PENDING:
          itemState.enabled = false;
          break;
      }
      // Order is an SL order and quick fill is disabled for SL orders
      switch (item.requestedOrderType) {
        case CubiqTS.RequestOrderType.SL_ASK:
        case CubiqTS.RequestOrderType.SL_ATRATE:
        case CubiqTS.RequestOrderType.SL_BID:
        case CubiqTS.RequestOrderType.TSL_ASK:
        case CubiqTS.RequestOrderType.TSL:
        case CubiqTS.RequestOrderType.TSL_BID:
          if (menuState.slQuickFillEnabled === "N") {
            itemState.enabled = false;
          }
          break;
      }

      if (menuState.pricingService != null && menuState.pricingService.isFwdRateApplicable(item)) {
        if (!menuState.pricingService.isFwdRateTradeable(item)) {
          itemState.enabled = false;
        }
      }

      // no detail fill for paused orders
      if (
        CubiqTS.PauseState.PAUSED === item.pauseState ||
        CubiqTS.PauseState.PAUSE_PENDING === item.pauseState
      ) {
        itemState.enabled = false;
      }

      // item is with external trader group
      if (itemState.enabled && item.deskId != null) {
        let tradingGroup = menuState.traderGroups.get(item.deskId.toString());

        if (tradingGroup && tradingGroup.isExternallyFilledFg) {
          itemState.enabled = false;
        }
      }

      if (itemState.enabled) {
        //   itemState.count++;

        // item is in transfer
        if (item.targetDeskId !== undefined && item.targetDeskId !== null) {
          itemState.warnings.show = true;
          itemState.warnings.transfer.display = true;
          itemState.warnings.transfer.ids.push(item.orderId.toString());
          itemState.warnings.transfer.orderRefs.push(item.orderRef);
        }
        // order is about to expire
        if (
          item.orderState === CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED ||
          item.orderState === CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED
        ) {
          itemState.warnings.show = true;
          itemState.warnings.expired.display = true;
          itemState.warnings.expired.ids.push(item.orderId.toString());
          itemState.warnings.expired.orderRefs.push(item.orderRef);
        }
        // order credit failed and waiting approval in DI
        if (
          item.creditRevalDetails instanceof CubiqTS.CreditRevalDetailsImpl &&
          item.creditRevalDetails != null &&
          item.creditRevalDetails !== undefined
        ) {
          let detail: CubiqTS.CreditRevalDetailsImpl =
            item.creditRevalDetails as CubiqTS.CreditRevalDetailsImpl;
          if (detail.lastCreditStatus !== undefined && detail.lastCreditStatus >= 0) {
            if (
              CubiqTS.CreditStatus.CREDIT_FAILED === detail.lastCreditStatus ||
              CubiqTS.CreditStatus.CREDIT_TIMEDOUT === detail.lastCreditStatus
            ) {
              itemState.warnings.show = true;
              itemState.warnings.credit_check.display = true;
              itemState.warnings.credit_check.ids.push(item.orderId.toString());
              itemState.warnings.credit_check.orderRefs.push(item.orderRef);
            }
          }
        }
      }
    };
  }

  public static get state(): any {
    return new BfxBlotterOrdersContextMenuItemQuickFillState();
  }
}
